import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import debounce from 'lodash/debounce';

import { useRecoilValue } from 'recoil';
import currentFormAtom from '../../../../../../Atoms/currentForm';

import {
  getOptionsAsync,
  verificatorAsync,
} from '../../../../../../Utils/engine/triggerApi';
import safelyParseJSON from '../../../../../../Utils/safelyParseJSON';
import {
  IterationContext,
  getFieldDynamicDataState,
  withResolvedParams,
} from '../../../../../../Utils/engine/iteration';

/**
 * Composant Autocomplete simple (valeur unique).
 * Charge ses options dynamiquement via API.
 * Peut être alimenté par un champ source (formData contient alors un objet JSON
 * avec codeRefValeur qui sert de paramètre à l'appel API).
 *
 * @param {object} schema - Configuration du champ (titre, config API, etc.)
 * @param {bool} required - Champ obligatoire ou non
 * @param {string} formData - Valeur actuelle du champ (peut être un JSON stringifié)
 * @param {function} onChange - Callback appelé quand la valeur change
 * @param {any} errorSchema - Erreurs de validation du champ
 * @param {function} dynamicLoading - Fonction qui gère le texte affiché pendant le chargement
 */
const BasicAutocomplete = ({
  schema,
  required,
  formData,
  onChange,
  errorSchema,
  dynamicLoading,
  formContext,
}) => {
  const currentForm = useRecoilValue(currentFormAtom);
  const iterationIndexes = useContext(IterationContext);
  // Options chargées par l'extension dynamicData (propres à l'itération courante)
  const dynamicState = getFieldDynamicDataState(schema, iterationIndexes);

  // Index gardés dans une ref : la recherche débouncée est créée une seule fois
  const iterationIndexesRef = useRef(iterationIndexes);
  iterationIndexesRef.current = iterationIndexes;

  // Config avec les paramètres "@value<chemin>" résolus depuis le formData
  // complet et l'itération courante (ex : le thème de la même démarche)
  const resolveConfig = (dataConfig) =>
    withResolvedParams(
      dataConfig,
      formContext?.getFormData?.(),
      iterationIndexesRef.current
    );

  // Texte saisi dans l'input (ce que l'utilisateur tape pour filtrer)
  const [data, setData] = useState('');
  const [loading, setLoading] = useState(false);

  const parsedFormData = safelyParseJSON(formData);

  // Valeur sélectionnée (l'id de l'option choisie)
  const [currentValue, setCurrentvalue] = useState(
    parsedFormData ? `${parsedFormData}` : ''
  );

  // Liste des options disponibles dans le dropdown
  const [options, setOptions] = useState([]);

  /**
   * Synchronise currentValue quand formData change depuis l'extérieur.
   * Cas typique : un champ source change de valeur et met à jour formData
   * avec un nouvel objet JSON contenant codeRefValeur.
   * Sans ce useEffect, currentValue garderait l'ancienne valeur
   * car useState ne se réinitialise pas automatiquement.
   */
  useEffect(() => {
    const parsed = safelyParseJSON(formData);
    const newValue = parsed?.codeRefValeur ? `${parsed.codeRefValeur}` : '';

    if (newValue !== currentValue) {
      setCurrentvalue(newValue);
      setData(''); // reset l'affichage du champ
      setOptions([]); // force le rechargement des options
    }
  }, [formData]);

  /**
   * Au premier montage du composant uniquement (tableau de dépendances vide []).
   * Si le champ a déjà une valeur (currentValue non vide) mais pas encore d'options,
   * appelle l'API verificator pour retrouver le libellé correspondant à cette valeur
   * et l'afficher correctement dans le champ.
   */
  useEffect(() => {
    if (currentValue.length > 0 && !options.length) {
      setLoading(true);
      verificatorAsync(
        resolveConfig(schema['@verificatorData']),
        currentValue
      ).then((filteredOptions) => {
        if (filteredOptions.length > 0 && filteredOptions?.[0]?.label) {
          setData(filteredOptions[0].label);
        }
        setOptions(filteredOptions);
        setLoading(false);
      });
    }
  }, []);

  /**
   * Se déclenche quand le timestamp de mise à jour des données dynamiques change
   * (géré via l'atom Recoil partagé).
   * Récupère les options pré-chargées depuis le schéma si disponibles.
   */
  useEffect(() => {
    if (dynamicState['@paramsAtFetch'] && dynamicState['@fetched']) {
      setOptions(dynamicState['@fetched']);
    }
  }, [currentForm.dynamicData.updatedTimestamp]);

  // Met à jour le texte affiché quand l'utilisateur tape dans le champ
  const onChangeInput = (e, newInputValue) => {
    setData(newInputValue);
  };

  /**
   * Appel API débounced (500ms) pour charger les options en fonction
   * de ce que l'utilisateur tape. Le debounce évite de lancer un appel
   * à chaque frappe clavier.
   */
  const getOptionsDelayed = useCallback(
    debounce((text, callback) => {
      setOptions([]);
      getOptionsAsync(
        resolveConfig(schema['@dynamicData']),
        text,
        currentValue
      ).then(callback);
    }, 500),
    []
  );

  /**
   * Se déclenche à chaque changement de "data" (texte saisi).
   * Lance le chargement des options via l'appel API débounced.
   */
  useEffect(() => {
    setLoading(true);
    getOptionsDelayed(`${data}`, (filteredOptions) => {
      setOptions(filteredOptions);
      setLoading(false);
    });
  }, [data, getOptionsDelayed]);

  return (
    <Autocomplete
      // Désactive le filtrage côté client, c'est l'API qui filtre
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.label || option}
      getOptionSelected={(option) => option.id || option}
      loading={options.length === 0}
      loadingText={dynamicLoading(
        schema['@dynamicData'],
        data,
        options,
        loading
      )}
      options={options}
      renderInput={(params) => (
        <TextField {...params} label={schema.title} required={required} />
      )}
      value={data}
      onChange={(_event, newValue) => {
        // Quand l'utilisateur sélectionne une option, on stocke son id
        if (newValue?.id) {
          setCurrentvalue(`${newValue.id}`);
          onChange(`${newValue.id}`, errorSchema, 2);
        } else {
          setCurrentvalue('');
          onChange('', errorSchema, 2);
        }
      }}
      onInputChange={(e, newInputValue) => onChangeInput(e, newInputValue)}
    />
  );
};

BasicAutocomplete.propTypes = {
  schema: PropTypes.shape({
    title: PropTypes.string.isRequired,
    '@dynamicData': PropTypes.object.isRequired,
    '@verificatorData': PropTypes.object.isRequired,
    description: PropTypes.string,
  }),
  required: PropTypes.bool,
  formData: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  errorSchema: PropTypes.any,
  dynamicLoading: PropTypes.func,
  formContext: PropTypes.shape({
    getFormData: PropTypes.func,
  }),
};

export default BasicAutocomplete;
